import React, { useMemo, useState } from "react";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";

export function DataTable({
  rows,
  columns,
  searchKeys = [],
  filters = [],
  testId = "data-table",
  emptyLabel,
}) {
  const { ui } = useOrignalUi();
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState(columns[0]?.key || "");
  const [dir, setDir] = useState("asc");
  const [activeFilters, setActiveFilters] = useState({});

  const filtered = useMemo(() => {
    let out = rows || [];
    const query = q.trim().toLowerCase();
    if (query) {
      out = out.filter((row) =>
        searchKeys.some((key) => String(row[key] ?? "").toLowerCase().includes(query)),
      );
    }
    for (const filter of filters) {
      const value = activeFilters[filter.key];
      if (value && value !== "all") {
        out = out.filter((row) => String(row[filter.key]) === value);
      }
    }
    const col = columns.find((c) => c.key === sortKey);
    out = [...out].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, q, sortKey, dir, activeFilters, columns, searchKeys, filters]);

  function toggleSort(key) {
    if (sortKey === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setDir("asc");
    }
  }

  const hasFilters = filters.length > 0;

  return (
    <div className="og-table-wrap" data-testid={testId}>
      <div className="og-toolbar">
        <input
          className="og-input"
          data-testid={`${testId}-search`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={ui.app.search}
        />
        {filters.map((filter) => (
          <select
            key={filter.key}
            className="og-input"
            data-testid={`${testId}-filter-${filter.key}`}
            value={activeFilters[filter.key] || "all"}
            onChange={(e) => setActiveFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))}
          >
            <option value="all">{filter.allLabel || ui.territory.all}</option>
            {filter.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ))}
        {hasFilters ? (
          <button
            type="button"
            className="og-btn og-btn--ghost"
            data-testid={`${testId}-reset`}
            onClick={() => {
              setQ("");
              setActiveFilters({});
            }}
          >
            {ui.app.reset}
          </button>
        ) : null}
      </div>
      <div className="og-table-scroll">
        <table className="og-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>
                  <button
                    type="button"
                    className="og-th"
                    data-testid={`${testId}-sort-${col.key}`}
                    onClick={() => toggleSort(col.key)}
                  >
                    {col.label}
                    {sortKey === col.key ? (dir === "asc" ? " ▲" : " ▼") : ""}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length} data-testid={`${testId}-empty`}>
                  {emptyLabel || ui.app.empty}
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id} onClick={() => row.onClick?.()} className={row.onClick ? "is-clickable" : ""}>
                  {columns.map((col) => (
                    <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
